import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Col, Row } from 'antd';
import { useTranslation } from 'react-i18next';
import { CandidateWidget } from '_templates/AnalyticsRecruitersInfo/AnalyticsRecruitersInfo';
import type { CandidateWidgetItem } from '_templates/AnalyticsRecruitersInfo';
import { Context } from '_templates/AnalyticsDashboard/analytics-dashboard';
import { ANALYTICS_TABS } from 'constants/constants';
import { TransitionStep } from '_templates/AnalyticsDashboard/TransitionStep/transition-step';
import { AnalyticsEmpty } from '_templates/AnalyticsDashboard/AnalyticsEmpty/analytics-empty';
import {
  analyticWidgets,
  type PassingStepWidget,
  type RejectionNumberWidget,
  type RejectionReasonWidget,
  type TransitionStepWidget,
} from 'actions/analyticActions';
import { PassingStep } from '_templates/AnalyticsDashboard/PassingStep/passing-step';
import { Rejection } from '_templates/AnalyticsDashboard/Rejection/rejection';
import { TOTAL_UNIQUE_CANDIDATE_CONFIG } from '_templates/AnalyticsDashboard/constans';
import { Reason } from '_templates/AnalyticsDashboard/Reason/reason';
import { totalUniqueCandidatesDataMapper } from '_templates/AnalyticsDashboard/helpers';
import {
  createInitialWidgetState,
  type AnalyticsAppliedFilters,
  type TotalUniqueCandidatesRawData,
} from '_templates/AnalyticsDashboard/types';
import Date from 'dist/images/common/icons/graph.svg';

export const AnalyticsTeamLeadsInfo = () => {
  const { t } = useTranslation();
  const { appliedFilters } = useContext(Context)!;

  const chosenTab = ANALYTICS_TABS.TEAM_LEADS;

  const [transitionStep, setTransitionStep] = useState(
    createInitialWidgetState<TransitionStepWidget['options']>([] as TransitionStepWidget['options'])
  );
  const [passingStep, setPassingStep] = useState(createInitialWidgetState<PassingStepWidget[]>([]));
  const [rejectionNumber, setRejectionNumber] = useState(createInitialWidgetState<RejectionNumberWidget[]>([]));
  const [rejectionReason, setRejectionReason] = useState(createInitialWidgetState<RejectionReasonWidget[]>([]));
  const [to, setTo] = useState<PassingStepWidget | Record<string, never>>({});
  const [from, setFrom] = useState<PassingStepWidget | Record<string, never>>({});

  const [totalCandidate, setTotalCandidate] = useState<CandidateWidgetItem[]>([TOTAL_UNIQUE_CANDIDATE_CONFIG]);

  const setLoading = () => {
    setRejectionNumber(createInitialWidgetState<RejectionNumberWidget[]>([]));
    setRejectionReason(createInitialWidgetState<RejectionReasonWidget[]>([]));
    setPassingStep(createInitialWidgetState<PassingStepWidget[]>([]));
    setTransitionStep(createInitialWidgetState<TransitionStepWidget['options']>([] as TransitionStepWidget['options']));
    setTotalCandidate(v => [{ ...v[0], loading: true }]);
  };

  const applyAnalyticFilter = useCallback((filterOptions: AnalyticsAppliedFilters) => {
    setLoading();

    analyticWidgets('total-unique-candidates', filterOptions, null).then(resp =>
      setTotalCandidate(totalUniqueCandidatesDataMapper(resp as TotalUniqueCandidatesRawData, t))
    );
    analyticWidgets('rejections-number', filterOptions, 'number').then(resp =>
      setRejectionNumber({ options: resp, loading: false })
    );
    analyticWidgets('rejection-reasons', filterOptions, 'reason').then(resp => {
      setRejectionReason({ options: resp, loading: false });
    });
    analyticWidgets('steps-transition', filterOptions, 'transition').then(resp =>
      setTransitionStep({ options: resp.options, empty: resp.empty, loading: false })
    );
    analyticWidgets('steps-passing-time', filterOptions, 'passing').then(resp => {
      setPassingStep({ options: resp, empty: !resp.filter(item => !!item.data).length, loading: false });
      setFrom(resp[0]);
      setTo(resp[resp.length - 1]);
    });
  }, []);

  useEffect(() => {
    applyAnalyticFilter(appliedFilters);
  }, [appliedFilters]);

  return (
    <>
      <div className="candidate">
        <Row gutter={[32, 0]}>
          <Col>
            <CandidateWidget className="fill-height" data={totalCandidate} />
          </Col>
          <Col>
            <Reason className="fill-height" options={rejectionReason.options} loading={rejectionReason.loading} />
          </Col>
        </Row>
      </div>
      {transitionStep.loading || (!transitionStep.loading && (!transitionStep.empty || !passingStep.empty)) ? (
        <>
          <TransitionStep
            options={transitionStep.options}
            loading={transitionStep.loading}
            empty={transitionStep.empty}
            role={chosenTab}
          />
          <PassingStep
            options={passingStep.options}
            loading={passingStep.loading}
            from={from}
            to={to}
            setFrom={setFrom}
            setTo={setTo}
          />
          <Rejection options={rejectionNumber.options} loading={rejectionNumber.loading} />
        </>
      ) : (
        <AnalyticsEmpty
          text={t('translation:analytics_team_leads_info.text_no_results')}
          title={t('translation:analytics_team_leads_info.title_no_results')}
          className="shadow"
          img={Date}
        />
      )}
    </>
  );
};
